"use client";
import clsx from "clsx";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useMemo, useState } from "react";

import FullSizeLoading from "components/FullSizeLoading";
import { NoSpaces } from "components/NoSpaces";
import ParkingLotStatusTag, { ParkingLotStatusTagStyle } from "components/ParkingLotStatusTag";
import { SortTableField, UnSortableField } from "components/ParkingTable";
import { SORT_DESTINATION, SortDestination } from "constants/sort";
import { SpaceDetail, SpaceEdgeValue } from "graphql/type.ts";
import { isEmptyArray } from "utils/array.util";
import { cn } from "utils/cn";
import { dateFormat } from "utils/date.util";
import { isEmptyString } from "utils/string.util";

const { ASC, DESC } = SORT_DESTINATION;

type UsageTimeData = {
  useStartDate: string;
  terminateDate?: string;
  consumer: { name: string; applicationId?: string };
  phoneNumber: string;
};

export type UsageRowData = {
  id: string;
  name: string; // 区画名
  isAcceptable: boolean; // 申込を受け付けるかどうか
  times: UsageTimeData[]; // 利用時間
  status: number; // 空き状況
};

const IsAcceptingApplications = ({ status }: { status: number }) => {
  const isAcceptingApplications = status == 1;

  return (
    <div
      className={cn(
        isAcceptingApplications ? "text-attention" : "text-black-light",
        "flex items-center gap-[5px] text-s"
      )}
    >
      <div className={cn("h-2.5 w-2.5 rounded-full", isAcceptingApplications ? "bg-attention" : "bg-text-disabled")} />
      {isAcceptingApplications ? "受付中" : "受付停止"}
    </div>
  );
};

const TableRow = ({ space: { id, name, spaceAvailableStatus, usages, status } }: { space: SpaceEdgeValue }) => {
  const pathname = usePathname();

  const contractors = useMemo(() => {
    return usages?.map((usage) => usage?.contractor);
  }, [usages]);

  const getStartDateStyle = (status: string) => {
    if (status === ParkingLotStatusTagStyle.FULL_TEMPORARY) {
      return "text-full font-bold";
    }

    return "";
  };

  const getTerminalDateStyle = (status: string, terminateDate?: string) => {
    if (status === ParkingLotStatusTagStyle.FULL_TEMPORARY) {
      return "text-full font-bold";
    }

    if (
      (status === ParkingLotStatusTagStyle.FULL_PLAN_CANCEL || status === ParkingLotStatusTagStyle.FULL_PLAN_END) &&
      !!terminateDate
    ) {
      return "text-green font-bold";
    }

    return "";
  };

  return (
    <Link
      href={`${pathname}/space/${id}/update`}
      className="grid min-h-[64px] grid-cols-usages items-center rounded border border-border bg-white py-2 shadow-table hover:border-text-gray"
    >
      <div className="grid h-full place-items-center border-r px-4">{name}</div>
      <div className="grid h-full place-items-center border-r">
        {spaceAvailableStatus && <ParkingLotStatusTag style={spaceAvailableStatus} />}
      </div>
      <div className="col-span-2 col-start-3 row-auto grid place-items-center border-r">
        {usages != null && usages?.length > 0 ? (
          usages?.map((usage, index) => {
            const endDate = usage?.approveCancelDate || usage?.terminateDate;

            return (
              <div
                className="flex min-h-[48px] w-full items-center justify-center border-b last:border-b-0"
                key={index}
              >
                <span className={cn("min-w-[84px] text-center", getStartDateStyle(spaceAvailableStatus ?? ""))}>
                  {dateFormat(usage?.useStartDate)}
                </span>
                <img src="/svg/rightArrow.svg" className="mx-6" alt="right arrow" />
                <span
                  className={cn("min-w-[84px] text-center", getTerminalDateStyle(spaceAvailableStatus ?? "", endDate))}
                >
                  {endDate ? dateFormat(endDate) : "未定"}
                </span>
              </div>
            );
          })
        ) : (
          <div className="flex min-h-[48px] w-full items-center justify-center border-b last:border-b-0">
            <span className="min-w-[84px] text-center">-</span>
            <img src="/svg/rightArrow.svg" className="mx-6" alt="right arrow" />
            <span className="min-w-[84px] text-center">-</span>
          </div>
        )}
      </div>
      <div className="grid place-items-center border-r">
        {!isEmptyArray(contractors) ? (
          contractors?.map((contractor, index) => {
            return contractor?.applicationId ? (
              <Link
                className={clsx(
                  "flex min-h-[48px] w-full items-center justify-start border-b px-8 text-primary last:border-b-0"
                )}
                key={index}
                href={`/customer/${contractor?.applicationId}`}
              >
                {contractor?.name == null ? <span>-</span> : <span>{contractor?.name}</span>}
              </Link>
            ) : (
              <div
                className={clsx("flex min-h-[48px] w-full items-center justify-start border-b px-8 last:border-b-0")}
                key={index}
              >
                {isEmptyString(contractor?.name) ? <span>-</span> : <span>{contractor?.name}</span>}
              </div>
            );
          })
        ) : (
          <div className="flex min-h-[48px] w-full items-center justify-start border-b px-8 last:border-b-0">
            <span>-</span>
          </div>
        )}
      </div>
      <div className="grid h-full place-items-center border-r">
        {!isEmptyArray(contractors) ? (
          contractors?.map((contractor, index) => (
            <div
              className={clsx("flex min-h-[48px] w-full items-center justify-start border-b px-8 last:border-b-0")}
              key={index}
            >
              {isEmptyString(contractor?.phoneNumber) ? <span>-</span> : <span>{contractor?.phoneNumber}</span>}
            </div>
          ))
        ) : (
          <div className="flex min-h-[48px] w-full items-center justify-start border-b px-8 last:border-b-0">
            <span>-</span>
          </div>
        )}
      </div>
      <div className="flex min-w-[110px]  items-center justify-center">
        {status != null && <IsAcceptingApplications status={status} />}
      </div>
    </Link>
  );
};

const SpacesTable = ({
  data,
  onSetSort,
  pathname,
}: {
  data?: SpaceEdgeValue[];
  onSetSort: (field: keyof SpaceDetail, direction: SortDestination) => void;
  pathname: string;
}) => {
  // 並び替えのフィールド
  const [sortField, setSortField] = useState<keyof SpaceDetail | null>(null);
  // 並び替えの方向
  const [sortDirection, setSortDirection] = useState<SortDestination | "">("");

  const onSort = (field: keyof SpaceDetail) => {
    const newSortDirection = sortField !== field ? DESC : sortDirection === ASC ? DESC : ASC;
    setSortField(field);
    setSortDirection(newSortDirection);
    onSetSort(field, newSortDirection);
  };

  const headers: { name: string; field?: keyof SpaceDetail }[] = [
    { name: "区画名", field: "name" },
    { name: "利用状況", field: "status" },
    { name: "利用開始日" },
    { name: "利用終了日" },
    { name: "契約者" },
    { name: "電話番号" },
    { name: "申込受付", field: "isAcceptable" },
  ];

  return (
    <div className="flex flex-col gap-1">
      <div className=" grid h-8 grid-cols-usages rounded bg-gray-light px-2">
        {headers.map(({ name, field }) =>
          field ? (
            <SortTableField<SpaceDetail>
              key={field}
              field={field}
              name={name}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={onSort}
            />
          ) : (
            <UnSortableField name={name} key={name} />
          )
        )}
      </div>
      {data == null ? (
        <div className="flex min-h-[300px] flex-1">
          <FullSizeLoading />
        </div>
      ) : data.length === 0 ? (
        <NoSpaces href={pathname + "/space/create"} />
      ) : (
        data.map((row, rowIndex) => <TableRow key={rowIndex} space={row} />)
      )}
    </div>
  );
};

export default SpacesTable;
